import React, { useState, useEffect } from "react";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Paper,
  CircularProgress, Chip, IconButton, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Tooltip, Alert, Snackbar, Select, MenuItem, FormControl, InputLabel, Tabs, Tab,
} from "@mui/material";
import { Delete, Edit, Visibility, Refresh, Search } from "@mui/icons-material";
import axios from "axios";
import { api_urls } from "../../../utils/api-urls";

const API = api_urls + "api/sanatani-hotel";

const SSBookings = () => {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [detailDialog, setDetailDialog] = useState(false);
  const [detailBooking, setDetailBooking] = useState(null);
  const [statusDialog, setStatusDialog] = useState(false);
  const [statusBookingId, setStatusBookingId] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [refundDialog, setRefundDialog] = useState(false);
  const [refundId, setRefundId] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");

  useEffect(() => { fetchBookings(); }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API + "/admin/bookings");
      if (res.data?.success) setBookings(res.data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleStatusUpdate = async () => {
    try {
      await axios.put(API + "/admin/booking/" + statusBookingId + "/status", { status: newStatus });
      setSnack({ open: true, msg: "Booking status updated!", severity: "success" });
      setStatusDialog(false);
      fetchBookings();
    } catch (e) { setSnack({ open: true, msg: "Error", severity: "error" }); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(API + "/admin/booking/" + deleteId);
      setSnack({ open: true, msg: "Booking deleted!", severity: "success" });
      setDeleteDialog(false);
      fetchBookings();
    } catch (e) { setSnack({ open: true, msg: "Error deleting", severity: "error" }); }
  };

  const handleRefund = async () => {
    try {
      await axios.put(API + "/admin/booking/" + refundId + "/refund", { refundAmount: parseFloat(refundAmount) });
      setSnack({ open: true, msg: "Refund processed!", severity: "success" });
      setRefundDialog(false);
      fetchBookings();
    } catch (e) { setSnack({ open: true, msg: "Error processing refund", severity: "error" }); }
  };

  const statusColors = { confirmed: "#4CAF50", pending: "#FF9800", cancelled: "#F44336", checked_in: "#2196F3", checked_out: "#9E9E9E", no_show: "#795548" };

  const filtered = bookings.filter(b => {
    if (tab !== "all" && b.bookingStatus !== tab) return false;
    if (!search) return true;
    return b.guestDetails?.name?.toLowerCase().includes(search.toLowerCase()) || 
      b.propertyId?.name?.toLowerCase().includes(search.toLowerCase());
  });

  if (loading) return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>📅 Bookings ({bookings.length})</Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchBookings}>Refresh</Button>
      </Box>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={"All (" + bookings.length + ")"} value="all" />
        <Tab label={"Pending (" + bookings.filter(b=>b.bookingStatus==="pending").length + ")"} value="pending" />
        <Tab label={"Confirmed (" + bookings.filter(b=>b.bookingStatus==="confirmed").length + ")"} value="confirmed" />
        <Tab label={"Cancelled (" + bookings.filter(b=>b.bookingStatus==="cancelled").length + ")"} value="cancelled" />
      </Tabs>

      <TextField size="small" placeholder="Search by guest or property name..." value={search} onChange={e => setSearch(e.target.value)}
        InputProps={{ startAdornment: <Search sx={{ color: "#999", mr: 1 }} /> }} sx={{ minWidth: 300, mb: 2 }} />

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell><b>#</b></TableCell>
              <TableCell><b>Guest</b></TableCell>
              <TableCell><b>Property</b></TableCell>
              <TableCell><b>Check In</b></TableCell>
              <TableCell><b>Check Out</b></TableCell>
              <TableCell><b>Rooms</b></TableCell>
              <TableCell><b>Amount</b></TableCell>
              <TableCell><b>Payment</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((b, i) => (
              <TableRow key={b._id} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell>
                  <Typography fontWeight={600} fontSize={13}>{b.guestDetails?.name || "Guest"}</Typography>
                  <Typography variant="caption" color="textSecondary">{b.guestDetails?.mobile || b.guestDetails?.phone || ""}</Typography>
                </TableCell>
                <TableCell>{b.propertyId?.name || "N/A"}</TableCell>
                <TableCell>{b.checkIn ? new Date(b.checkIn).toLocaleDateString("en-IN") : "-"}</TableCell>
                <TableCell>{b.checkOut ? new Date(b.checkOut).toLocaleDateString("en-IN") : "-"}</TableCell>
                <TableCell>{b.roomsBooked || (Array.isArray(b.rooms) ? b.rooms.reduce((sum, r) => sum + (r.quantity || 1), 0) : b.rooms) || 1}</TableCell>
                <TableCell><b>₹{b.finalAmount || 0}</b></TableCell>
                <TableCell><Chip label={b.paymentStatus || "pending"} size="small" color={b.paymentStatus === "paid" ? "success" : "warning"} /></TableCell>
                <TableCell>
                  <Chip label={b.bookingStatus?.replace(/_/g, " ")} size="small" 
                    sx={{ backgroundColor: (statusColors[b.bookingStatus] || "#ccc") + "20", color: statusColors[b.bookingStatus] || "#666", fontWeight: 600, textTransform: "capitalize" }} />
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    <Tooltip title="View"><IconButton size="small" onClick={() => { setDetailBooking(b); setDetailDialog(true); }}><Visibility fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Change Status"><IconButton size="small" color="primary" onClick={() => { setStatusBookingId(b._id); setNewStatus(b.bookingStatus); setStatusDialog(true); }}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Refund"><IconButton size="small" color="warning" onClick={() => { setRefundId(b._id); setRefundAmount(String(b.finalAmount||0)); setRefundDialog(true); }}>₹</IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => { setDeleteId(b._id); setDeleteDialog(true); }}><Delete fontSize="small" /></IconButton></Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length === 0 && <Box py={4} textAlign="center"><Typography color="textSecondary">No bookings found</Typography></Box>}
      </Paper>

      {/* Status Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)}>
        <DialogTitle>Update Booking Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select value={newStatus} onChange={e => setNewStatus(e.target.value)} label="Status">
              {["pending", "confirmed", "checked_in", "checked_out", "cancelled", "no_show"].map(s => (
                <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s.replace(/_/g, " ")}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions><Button onClick={() => setStatusDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleStatusUpdate}>Update</Button></DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialog} onClose={() => setRefundDialog(false)}>
        <DialogTitle>Process Refund</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Refund Amount (₹)" type="number" value={refundAmount} onChange={e => setRefundAmount(e.target.value)} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions><Button onClick={() => setRefundDialog(false)}>Cancel</Button><Button variant="contained" color="warning" onClick={handleRefund}>Process Refund</Button></DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>⚠️ Delete Booking</DialogTitle>
        <DialogContent><Typography>Permanently delete this booking?</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDeleteDialog(false)}>Cancel</Button><Button variant="contained" color="error" onClick={handleDelete}>Delete</Button></DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Booking Details</DialogTitle>
        <DialogContent>
          {detailBooking && (
            <Grid container spacing={2}>
              <Grid item xs={6}><Typography variant="body2" color="textSecondary">Guest</Typography><Typography fontWeight={600}>{detailBooking.guestDetails?.name}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="textSecondary">Phone</Typography><Typography>{detailBooking.guestDetails?.mobile || detailBooking.guestDetails?.phone || "-"}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="textSecondary">Email</Typography><Typography>{detailBooking.guestDetails?.email || "-"}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="textSecondary">Property</Typography><Typography fontWeight={600}>{detailBooking.propertyId?.name}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="textSecondary">Check In</Typography><Typography>{detailBooking.checkIn ? new Date(detailBooking.checkIn).toLocaleDateString("en-IN") : "-"}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="textSecondary">Check Out</Typography><Typography>{detailBooking.checkOut ? new Date(detailBooking.checkOut).toLocaleDateString("en-IN") : "-"}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="textSecondary">Rooms</Typography><Typography>{detailBooking.roomsBooked || (Array.isArray(detailBooking.rooms) ? detailBooking.rooms.reduce((s, r) => s + (r.quantity || 1), 0) : 1)}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="textSecondary">Guests</Typography><Typography>{detailBooking.guestDetails?.numberOfAdults || detailBooking.guests || 1}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="textSecondary">Amount</Typography><Typography fontWeight={700}>₹{detailBooking.finalAmount}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="textSecondary">Payment</Typography><Chip label={detailBooking.paymentStatus || "pending"} size="small" color={detailBooking.paymentStatus==="paid"?"success":"warning"} /></Grid>
              <Grid item xs={6}><Typography variant="body2" color="textSecondary">Status</Typography><Chip label={detailBooking.bookingStatus} size="small" color="primary" /></Grid>
              <Grid item xs={12}><Typography variant="body2" color="textSecondary">Booked At</Typography><Typography>{detailBooking.createdAt ? new Date(detailBooking.createdAt).toLocaleString("en-IN") : "-"}</Typography></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailDialog(false)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({...snack, open: false})} anchorOrigin={{vertical:"top",horizontal:"right"}}>
        <Alert severity={snack.severity} onClose={() => setSnack({...snack, open: false})}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SSBookings;
